import { object, string, useForm } from "@website/form/hook/form.hooks";
import { useJwtToken } from "@website/hooks/auth-manager";
import { addEventToDataLayerGT3 } from "@website/tracking/tracking.gtm";
import { DATA_LAYER_EVENTS } from "@website/tracking/tracking.types";
import { getChannel } from "@website/utils/channel/get-channel";
import { datalayerAppName } from "@website/widget/telesales/constants";
import { useSendToTelesales } from "@website/widget/telesales/telesales-form/queries";
import {
  TelesalesFormPropertiesType,
  TelesalesFromDataType
} from "@website/widget/telesales/telesales-form/types";
import { useCallback, useState } from "react";

export const useTelesales = ({
  onClose,
  requestData,
  storageKey,
  onSuccess,
  onSubmitSuccess,
  reasons
}: Pick<
  TelesalesFormPropertiesType,
  | "onClose"
  | "requestData"
  | "storageKey"
  | "reasons"
  | "onSuccess"
  | "onSubmitSuccess"
>) => {
  const { mutateAsync, isPending } = useSendToTelesales();

  const { phoneNumber, name } = useJwtToken();
  const [isPhoneStep, setIsPhoneStep] = useState<boolean>(true);

  const { control, handleSubmit } = useForm({
    mode: "onBlur",
    defaultValues: {
      freeTransferMobileNumber: phoneNumber?.replace?.("+98", "0") || ""
    },
    validations: object({
      freeTransferMobileNumber: string().required().mobile()
    })
  });

  const handleSetEvent = useCallback(
    async (data: TelesalesFromDataType) => {
      if (isPhoneStep) {
        setIsPhoneStep(false);
        return;
      }
      onClose();
      const reasonSelected = [];
      for (const property in data) {
        if (
          data[property] &&
          property !== "reason" &&
          property !== "freeTransferMobileNumber"
        ) {
          reasonSelected.push(reasons[property]);
        }
      }
      localStorage.setItem(storageKey, Date.now()?.toString());
      addEventToDataLayerGT3(DATA_LAYER_EVENTS.TELESALES_SUBMIT, {
        item: [
          {
            Item_brand: datalayerAppName[requestData.app],
            phone: data?.freeTransferMobileNumber
          }
        ]
      });
      await mutateAsync({
        channel: getChannel(),
        phone: data?.freeTransferMobileNumber,
        description: data?.reason || "",
        fullname: name || "",
        reasons: reasonSelected,
        ...requestData
      });
      onSuccess?.();
      onSubmitSuccess();
    },
    [
      onSubmitSuccess,
      isPhoneStep,
      mutateAsync,
      name,
      onClose,
      onSuccess,
      reasons,
      requestData,
      storageKey
    ]
  );

  return {
    handleSetEvent,
    isPhoneStep,
    control,
    isPending,
    handleSubmit
  };
};
